import React, { useEffect, useState, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { RadioButton } from 'primereact/radiobutton';
import TypeOfWorkDropdown from './TypeOfWorkDropdown';
import ItemCodeDropdown from '../../../../../../../components/common/ItemCodeDropdown';
import WarrantyPaymentTypeRadio from '../../../../../../../components/common/WarrantyPaymentTypeRadio';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';
import { useGetWarrantyClaimsItemByIdQuery, useGetWarrantyClaimsByIdQuery } from '../../../../../../../api';
import { useGetStockByPartCodeQuery } from '../../../../../../../api/endpoints/dealerPartsCatalog';
import ExternalPartsDropdown from './ExternalPartsDropdown';
import { WarrantyClaimStatus } from '../../../../../../../utils/warrantyClaimsEnums';

const PART_SOURCE = {
    KARSAN: 0,
    EXTERNAL_STOCK: 1
};

//TODO : Kullanıldığı yerleri tekrar kontrol et.
const calculateItemPrice = (itemCode, warrantyType, currentPartSource, defaultValue = null) => {
    if (!itemCode || typeof itemCode !== 'object') {
        return defaultValue ?? 0;
    }

    if (currentPartSource === PART_SOURCE.EXTERNAL_STOCK) {
        // Harici parça için direkt itemPrice kullan (retailPrice yok)
        return itemCode?.itemPrice ?? defaultValue ?? 0;
    } else {
        // Karsan parçası veya diğer durumlar için mevcut mantık
        if (warrantyType === 1) {
            return itemCode.retailPrice ?? itemCode?.itemPrice ?? defaultValue ?? 0;
        } else if (warrantyType === 2) {
            return itemCode.karsanPrice ?? itemCode?.itemPrice ?? defaultValue ?? 0;
        } else {
            return itemCode?.itemPrice ?? defaultValue ?? 0;
        }
    }
};

const PartFormDialog = ({
    branchCode,
    branchSiteCode,
    claimId,
    dialogVisible,
    hideDialog,
    onSave,
    onDelete,
    editingPart = null,
    claimStatus,
    warrType,
    isAcceptedRejectedTab = false,
    disabledStatuses,
    authorizationCheck,
    onUpdateSuccess,
    packageId,
    dealerId,
    dealerAddressId
}) => {
    const { th, getCurrentLanguageId, getCurrentLocale } = useLanguage();
    const initialForm = {
        itemCode: null,
        itemCodeDesc: '',
        description: '',
        quantity: '',
        itemPrice: '',
        itemPriceCurr: '',
        mainPart: 0,
        typeOfWork: 1,
        warrantyType: null,
        warrClaimId: Number(claimId),
        bosBranchSiteCode: 0,
        isScrap: null
    };

    const [formData, setFormData] = useState(initialForm);
    const [submitted, setSubmitted] = useState(false);
    const [isFormReady, setIsFormReady] = useState(false);
    const [partSource, setPartSource] = useState(PART_SOURCE.KARSAN);
    const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
    // BE'den gelen orijinal değerleri ve manuel değişiklikleri takip et
    const [originalBeValues, setOriginalBeValues] = useState({ acceptedAmount: null, totalAmount: null });
    const [manuallyChanged, setManuallyChanged] = useState({ acceptedAmount: false, totalAmount: false });
    const [displayValues, setDisplayValues] = useState({ acceptedAmount: null, totalAmount: null });

    // Get warranty claims item by ID when editing
    const { data: warrantyClaimsItemById, isLoading: isLoadingItem, refetch: refetchItemById } = useGetWarrantyClaimsItemByIdQuery({ id: editingPart?.id, LanguageId: getCurrentLanguageId() }, { skip: !editingPart?.id });

    const { data: warrantyClaimData } = useGetWarrantyClaimsByIdQuery({ id: claimId, languageId: getCurrentLanguageId() }, { skip: !claimId || !editingPart });
    const isWarrantyTeam = warrantyClaimData?.isWarrantyTeam || false;
    const isInApprovalProcess = warrantyClaimData?.status === WarrantyClaimStatus.WAITING;

    useEffect(() => {
        if (!dialogVisible) {
            setFormData(initialForm);
            setSubmitted(false);
            setPartSource(PART_SOURCE.KARSAN);
            setOriginalBeValues({ acceptedAmount: null, totalAmount: null });
            setManuallyChanged({ acceptedAmount: false, totalAmount: false });
            setDisplayValues({ acceptedAmount: null, totalAmount: null });
            setDeleteConfirmVisible(false);
        }
    }, [dialogVisible]);

    const selectedPartCode = useMemo(() => {
        if (formData.itemCode && typeof formData.itemCode === 'object') {
            return formData.itemCode.itemCode;
        }
        return null;
    }, [formData.itemCode]);

    const { data: stockResponse, isLoading: isLoadingStock } = useGetStockByPartCodeQuery(
        {
            partCode: selectedPartCode,
            bosBranchCode: branchCode,
            bosBranchSiteCode: branchSiteCode
        },
        { skip: !selectedPartCode || partSource === PART_SOURCE.EXTERNAL_STOCK }
    );

    const stockQuantity = React.useMemo(() => {
        if (partSource === PART_SOURCE.EXTERNAL_STOCK) {
            if (formData.itemCode && typeof formData.itemCode === 'object') {
                return formData.itemCode.quantity ?? 0;
            }
            return 0;
        }

        // Karsan parçası seçiliyse backend stock response'tan göster
        if (!selectedPartCode || isLoadingStock) return 0;
        return stockResponse?.data?.items?.[0]?.quantity ?? 0;
    }, [partSource, formData.itemCode, selectedPartCode, isLoadingStock, stockResponse]);

    // IsScrap alanının görünür olması gereken durumları kontrol et
    const shouldShowIsScrap = () => {
        // Create modunda (yeni kayıt) hiç gösterme
        if (!editingPart) return false;

        // Kabul edilenler/reddedilenler tabında edit modunda görünsün ama disabled olsun
        if (isAcceptedRejectedTab && editingPart) return true;

        // Garanti talebi onay sürecindeyse ve edit modunda ise görünsün
        if (claimStatus === 'APPROVAL_PROCESS' && editingPart) return true;

        return false;
    };

    const shouldDisableIsScrap = () => {
        return disabledStatuses;
    };

    const isIsScrapRequired = () => {
        return shouldShowIsScrap() && !isAcceptedRejectedTab && editingPart;
    };

    useEffect(() => {
        setSubmitted(false);
        setIsFormReady(false);

        if (editingPart) {
            // Use getById data if available, otherwise fall back to editingPart data
            const itemData = warrantyClaimsItemById?.data || editingPart;

            // For editing, ensure all required data is available
            const requiredFields = ['id', 'itemCode', 'quantity', 'typeOfWork'];
            const hasAllRequiredFields = requiredFields.every((field) => itemData[field] !== undefined && itemData[field] !== null);

            if (hasAllRequiredFields) {
                const incomingPartBrand = itemData.partBrand ?? PART_SOURCE.KARSAN;
                setPartSource(incomingPartBrand);

                // BE'den gelen orijinal değerleri sakla
                setOriginalBeValues({
                    acceptedAmount: itemData.acceptedAmount,
                    totalAmount: itemData.totalAmount
                });

                // Display değerlerini de BE'den gelen değerlerle başlat
                setDisplayValues({
                    acceptedAmount: itemData.acceptedAmount,
                    totalAmount: itemData.totalAmount
                });

                // Manuel değişiklik flag'lerini sıfırla
                setManuallyChanged({
                    acceptedAmount: false,
                    totalAmount: false
                });

                setFormData({
                    id: itemData.id,
                    acceptedAmount: itemData.acceptedAmount,
                    totalAmount: itemData.totalAmount,
                    itemCode: itemData.itemCode,
                    itemCodeDesc: itemData.itemCodeDesc,
                    description: itemData.description || '',
                    quantity: Number(itemData.quantity || 0),
                    itemPrice: itemData.itemPrice,
                    itemPriceCurr: itemData.itemPriceCurr,
                    mainPart: itemData.mainPart,
                    typeOfWork: itemData.typeOfWork,
                    warrantyType: itemData.warrantyType,
                    warrClaimId: Number(claimId),
                    bosBranchSiteCode: itemData.bosBranchSiteCode,
                    isActive: itemData.isActive,
                    isScrap: itemData.isScrap || null
                });
                setIsFormReady(true);
            }
        } else {
            // For new item, form is ready immediately
            setFormData({
                ...initialForm,
                warrClaimId: Number(claimId)
            });
            setPartSource(PART_SOURCE.KARSAN);
            setOriginalBeValues({ acceptedAmount: null, totalAmount: null });
            setManuallyChanged({ acceptedAmount: false, totalAmount: false });
            setDisplayValues({ acceptedAmount: null, totalAmount: null });

            setIsFormReady(true);
        }
    }, [editingPart, dialogVisible, claimId, warrantyClaimsItemById]);

    // Create modunda itemCode seçildiğinde fiyat güncelle
    useEffect(() => {
        if (formData.itemCode && !editingPart && typeof formData.itemCode === 'object') {
            const calculatedPrice = calculateItemPrice(formData.itemCode, formData.warrantyType, partSource);

            setFormData((prev) => ({
                ...prev,
                itemCodeDesc: formData.itemCode.title || '',
                itemPrice: calculatedPrice,
                itemPriceCurr: formData.itemCode?.itemPriceCurr || 'EUR'
            }));
        }
    }, [formData.itemCode, editingPart, formData.warrantyType, partSource]);

    const handleChange = (value, key) => {
        if (key === 'itemCode') {
            if (value && typeof value === 'object') {
                const calculatedPrice = calculateItemPrice(value, formData.warrantyType, partSource, 0);

                setFormData((prev) => ({
                    ...prev,
                    itemCode: value,
                    itemCodeDesc: value.title || '',
                    itemPrice: calculatedPrice,
                    itemPriceCurr: value.itemPriceCurr || 'EUR'
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    itemCode: null,
                    itemCodeDesc: '',
                    itemPrice: '',
                    itemPriceCurr: ''
                }));
            }
        } else if (key === 'warrantyType') {
            // warrantyType değiştiğinde fiyatı güncelle
            setFormData((prev) => {
                if (prev.itemCode && typeof prev.itemCode === 'object') {
                    const calculatedPrice = calculateItemPrice(prev.itemCode, value, partSource);

                    return {
                        ...prev,
                        warrantyType: value,
                        itemPrice: calculatedPrice
                    };
                }
                return {
                    ...prev,
                    warrantyType: value
                };
            });
        } else if (key === 'quantity' && editingPart) {
            const newQuantity = Number(value) || 0;
            const itemPrice = formData.itemPrice || 0;
            const newTotalAmount = Math.round(newQuantity * itemPrice * 100) / 100;

            // Ekranda gösterilecek değerleri güncelle (sadece manuel değişiklik yapılmamışsa)
            setDisplayValues((prev) => ({
                totalAmount: manuallyChanged.totalAmount ? prev.totalAmount : newTotalAmount,
                acceptedAmount: manuallyChanged.acceptedAmount ? prev.acceptedAmount : newTotalAmount
            }));

            setFormData((prev) => ({
                ...prev,
                quantity: newQuantity
            }));
        } else if (key === 'acceptedAmount') {
            // Manuel olarak acceptedAmount değiştirildi
            setManuallyChanged((prev) => ({ ...prev, acceptedAmount: true }));
            setDisplayValues((prev) => ({ ...prev, acceptedAmount: value }));
            setFormData((prev) => ({ ...prev, acceptedAmount: value }));
        } else if (key === 'totalAmount') {
            // Manuel olarak totalAmount değiştirildi
            setManuallyChanged((prev) => ({ ...prev, totalAmount: true }));
            setDisplayValues((prev) => ({ ...prev, totalAmount: value }));
            setFormData((prev) => ({ ...prev, totalAmount: value }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [key]: key === 'quantity' ? Number(value) : value
            }));
        }
    };

    const handleSave = async () => {
        setSubmitted(true);
        // Garanti ekibi onay sürecinde ise warrantyType validasyonu atlanır (alan gizli)
        const shouldSkipWarrantyTypeValidation = isWarrantyTeam && isInApprovalProcess;
        const isValid = formData.itemCode && formData.quantity && formData.typeOfWork && (shouldSkipWarrantyTypeValidation || formData.warrantyType) && (!isIsScrapRequired() || formData.isScrap !== null);
        if (!isValid) return;

        const basePayload = {
            ...formData,
            partBrand: partSource,
            quantity: Number(formData.quantity),
            typeOfWork: Number(formData.typeOfWork),
            warrantyType: Number(formData.warrantyType),
            // itemCode değerini doğru şekilde ayarla - edit modunda string, create modunda object
            itemCode: typeof formData.itemCode === 'object' ? formData.itemCode.itemCode : formData.itemCode,
            // itemCodeDesc'i de payloada ekle
            itemCodeDesc: formData.itemCodeDesc,
            description: formData.description || null
        };

        // acceptedAmount ve totalAmount için doğru değerleri belirle
        if (editingPart) {
            // Manuel değişiklik yapıldıysa o değeri kullan, yoksa BE'den gelen orijinal değeri kullan
            basePayload.acceptedAmount = manuallyChanged.acceptedAmount ? formData.acceptedAmount : originalBeValues.acceptedAmount;
            basePayload.totalAmount = manuallyChanged.totalAmount ? formData.totalAmount : originalBeValues.totalAmount;
        }

        if (shouldShowIsScrap() && !isAcceptedRejectedTab && formData.isScrap !== null) {
            basePayload.isScrap = formData.isScrap;
        }

        const result = await onSave(basePayload);

        // Eğer update işlemi başarılıysa ve editingPart varsa, getById endpoint'ini çağır
        if (result && editingPart && onUpdateSuccess) {
            await refetchItemById();
            onUpdateSuccess();
        }
    };

    const handleDelete = () => {
        setDeleteConfirmVisible(true);
    };

    const handleConfirmDelete = async () => {
        setDeleteConfirmVisible(false);

        if (onDelete && editingPart) {
            await onDelete(editingPart);
        }
    };

    const isPackageIdValid = packageId != null && packageId !== 0;
    const isDeleteDisabled = disabledStatuses || isPackageIdValid;
    const isSaveDisabled = (disabledStatuses || (editingPart && isPackageIdValid)) && !isWarrantyTeam;

    const footer = (
        <div className="flex justify-content-between align-items-center">
            {editingPart && !isWarrantyTeam && <Button label={th('warrantyClaims.detail.partDialog.delete')} icon="pi pi-trash" className="p-button-danger" onClick={handleDelete} disabled={isDeleteDisabled} />}
            <div className="flex gap-2 justify-content-end w-full">
                <Button label={th('warrantyClaims.detail.partDialog.cancel')} icon="pi pi-times" onClick={hideDialog} outlined />
                <Button label={editingPart ? th('warrantyClaims.detail.partDialog.update') : th('warrantyClaims.detail.partDialog.create')} icon="pi pi-check" onClick={handleSave} autoFocus disabled={isSaveDisabled} />
            </div>
        </div>
    );

    const KARSAN_GUARANTEE_CODE = '2';
    return (
        <>
            <Dialog visible={dialogVisible && isFormReady && !isLoadingItem} style={{ width: '40vw' }} header={th('warrantyClaims.detail.partDialog.title')} modal className="p-fluid" footer={footer} onHide={hideDialog}>
                <p className="text-sm mb-3 text-color-secondary">
                    <strong>Not:</strong> {th('warrantyClaims.detail.partDialog.note')}
                </p>

            <div className="field mb-3">
                <label className="font-bold block mb-2">{th('warrantyClaims.detail.partDialog.partSource')} *</label>

                <div className="flex gap-4 align-items-center">
                    <div className="flex align-items-center">
                        <RadioButton
                            inputId="partSourceKarsan"
                            name="partSource"
                            value={PART_SOURCE.KARSAN}
                            onChange={(e) => {
                                setPartSource(e.value);
                                handleChange(null, 'itemCode');
                                handleChange(null, 'warrantyType');
                            }}
                            checked={partSource === PART_SOURCE.KARSAN}
                            disabled={disabledStatuses}
                        />
                        <label htmlFor="partSourceKarsan" className="ml-2">
                            {th('warrantyClaims.detail.partDialog.partSourceOptions.karsan')}
                        </label>
                    </div>

                    <div className="flex align-items-center">
                        <RadioButton
                            inputId="partSourceExternal"
                            name="partSource"
                            value={PART_SOURCE.EXTERNAL_STOCK}
                            onChange={(e) => {
                                setPartSource(e.value);
                                handleChange(null, 'itemCode');
                                handleChange(null, 'warrantyType');
                            }}
                            checked={partSource === PART_SOURCE.EXTERNAL_STOCK}
                            disabled={disabledStatuses}
                        />
                        <label htmlFor="partSourceExternal" className="ml-2">
                            {th('warrantyClaims.detail.partDialog.partSourceOptions.externalStock')}
                        </label>
                    </div>
                </div>
            </div>

            <div className="field mb-2">
                <label className="font-bold block mb-2">{th('warrantyClaims.detail.partDialog.partCode')} *</label>
                {partSource === PART_SOURCE.KARSAN ? (
                    <ItemCodeDropdown value={formData.itemCode} onChange={(val) => handleChange(val, 'itemCode')} className={submitted && !formData.itemCode ? 'p-invalid' : ''} disabled={disabledStatuses} claimId={claimId} />
                ) : (
                    <ExternalPartsDropdown
                        value={formData.itemCode}
                        dealerId={dealerId}
                        dealerAddressId={dealerAddressId}
                        onChange={(val) => handleChange(val, 'itemCode')}
                        className={submitted && !formData.itemCode ? 'p-invalid' : ''}
                        disabled={disabledStatuses}
                    />
                )}
                {submitted && !formData.itemCode && <small className="p-error">{th('warrantyClaims.detail.partDialog.validation.partCodeRequired')}</small>}
            </div>

            <div className="field mb-2">
                <label>{th('warrantyClaims.detail.partDialog.partName')}</label>
                <InputText value={formData.itemCodeDesc || ''} disabled />
            </div>

            <div className="field mb-2">
                <label>{th('warrantyClaims.detail.partDialog.description')}</label>
                <InputText value={formData.description || ''} onChange={(e) => handleChange(e.target.value, 'description')} disabled={disabledStatuses} />
            </div>

            <div className="grid">
                {!editingPart && (
                    <div className="col-12 md:col-6">
                        <div className="field mb-2">
                            <label>{th('parts.stock')}</label>
                            <InputText value={partSource === PART_SOURCE.KARSAN && isLoadingStock ? '-' : String(stockQuantity)} disabled />
                        </div>
                    </div>
                )}
                <div className={`col-12 ${!editingPart ? 'md:col-6' : ''}`}>
                    <div className="field mb-2">
                        <label className="required">{th('warrantyClaims.detail.partDialog.quantity')} *</label>
                        <InputNumber
                            value={formData.quantity}
                            onChange={(e) => handleChange(e.value, 'quantity')}
                            mode="decimal"
                            minFractionDigits={0}
                            maxFractionDigits={0}
                            min={0}
                            className={submitted && !formData.quantity ? 'p-invalid' : ''}
                            disabled={disabledStatuses}
                        />
                        {submitted && !formData.quantity && <small className="p-error">{th('warrantyClaims.detail.partDialog.validation.quantityRequired')}</small>}
                    </div>
                </div>
            </div>

            <div className="field mb-2">
                <label>{th('warrantyClaims.detail.partDialog.partPrice')}</label>
                <InputText value={`${formData.itemPrice || ''} ${formData.itemPriceCurr || ''}`} disabled />
            </div>

            {editingPart && (
                <>
                    <div className="field mb-2">
                        <label>{th('warrantyClaims.detail.labor.totalAmount')}</label>
                        <InputText value={displayValues.totalAmount || ''} disabled />
                    </div>

                    <div className="field mb-2">
                        <label>{th('warrantyClaims.detail.partTable.acceptedAmount')}</label>
                        <InputNumber
                            value={displayValues.acceptedAmount || 0}
                            onChange={(e) => handleChange(e.value || 0, 'acceptedAmount')}
                            mode="decimal"
                            locale={getCurrentLocale()}
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            step={0.01}
                            disabled={!authorizationCheck && !isWarrantyTeam}
                        />
                    </div>
                </>
            )}

            {!(isWarrantyTeam && isInApprovalProcess) && (
                <div className="field mb-2">
                    <label>{th('warrantyClaims.detail.partDialog.warrantyType')} *</label>
                    <WarrantyPaymentTypeRadio
                        value={formData.warrantyType}
                        onChange={(val) => handleChange(val, 'warrantyType')}
                        className={submitted && !formData.warrantyType ? 'p-invalid' : ''}
                        disabled={disabledStatuses}
                        excludeOptions={partSource === PART_SOURCE.EXTERNAL_STOCK ? [...(warrType === 'MSG' ? ['2'] : []), KARSAN_GUARANTEE_CODE] : warrType === 'MSG' ? ['2'] : []}
                        warrType={warrType}
                    />
                    {submitted && !formData.warrantyType && <small className="p-error">{th('warrantyClaims.detail.partDialog.validation.warrantyTypeRequired')}</small>}
                </div>
            )}

            <div className="field mb-2">
                <label>{th('warrantyClaims.detail.partDialog.mainPart')} *</label>
                <div className="flex gap-3 align-items-center">
                    <div className="flex align-items-center">
                        <RadioButton inputId="mainYes" name="mainPart" value={1} onChange={(e) => handleChange(e.value, 'mainPart')} checked={formData.mainPart === 1} disabled={disabledStatuses} />
                        <label htmlFor="mainYes" className="ml-2">
                            {th('warrantyClaims.detail.partDialog.yes')}
                        </label>
                    </div>
                    <div className="flex align-items-center">
                        <RadioButton inputId="mainNo" name="mainPart" value={0} onChange={(e) => handleChange(e.value, 'mainPart')} checked={formData.mainPart === 0} disabled={disabledStatuses} />
                        <label htmlFor="mainNo" className="ml-2">
                            {th('warrantyClaims.detail.partDialog.no')}
                        </label>
                    </div>
                </div>
            </div>

            <div className="field mb-2">
                <label>{th('warrantyClaims.detail.partDialog.workType')}*</label>
                <TypeOfWorkDropdown value={formData.typeOfWork} onChange={(val) => handleChange(val, 'typeOfWork')} className={submitted && !formData.typeOfWork ? 'p-invalid' : ''} disabled={disabledStatuses} />
                {submitted && !formData.typeOfWork && <small className="p-error">{th('warrantyClaims.detail.partDialog.validation.workTypeRequired')}</small>}
            </div>

            {shouldShowIsScrap() && (
                <div className="field mb-2">
                    <label className="font-bold block mb-2">
                        {th('warrantyClaims.detail.partDialog.isScrap')}
                        {isIsScrapRequired() && ' *'}
                    </label>
                    <div className="flex gap-4">
                        <div className="flex align-items-center">
                            <RadioButton inputId="keepPart" name="isScrap" value={1} onChange={(e) => handleChange(e.value, 'isScrap')} checked={formData.isScrap === 1} disabled={shouldDisableIsScrap() && !isWarrantyTeam} />
                            <label htmlFor="keepPart" className="ml-2">
                                {th('warrantyClaims.detail.partDialog.keepPart')}
                            </label>
                        </div>
                        <div className="flex align-items-center">
                            <RadioButton inputId="scrapPart" name="isScrap" value={2} onChange={(e) => handleChange(e.value, 'isScrap')} checked={formData.isScrap === 2} disabled={shouldDisableIsScrap() && !isWarrantyTeam} />
                            <label htmlFor="scrapPart" className="ml-2">
                                {th('warrantyClaims.detail.partDialog.scrapPart')}
                            </label>
                        </div>
                    </div>
                    {submitted && !formData.isScrap && isIsScrapRequired() && <small className="p-error">{th('warrantyClaims.detail.partDialog.validation.isScrapRequired')}</small>}
                </div>
            )}
            </Dialog>
            <Dialog
                visible={deleteConfirmVisible}
                style={{ width: '28rem' }}
                header={th('warrantyClaims.detail.partTable.deleteHeader')}
                modal
                onHide={() => setDeleteConfirmVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label={th('common.cancel')} onClick={() => setDeleteConfirmVisible(false)} outlined />
                        <Button label={th('common.delete')} onClick={handleConfirmDelete} className="p-button-danger" />
                    </div>
                }
            >
                <div className="flex align-items-center gap-3">
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.5rem' }} />
                    <span>{th('warrantyClaims.detail.partTable.deleteConfirm')}</span>
                </div>
            </Dialog>
        </>
    );
};

export default PartFormDialog;
